import { BitmapCoordinatesRenderingScope } from 'fancy-canvas'
import { Time } from 'lightweight-charts'
import DrawingBase from '../drawing.base'
import { DrawingOptions, DrawingSettingGroup, DrawingTypes, SettingValues } from '../drawing.interface'
import { scaledCoordinate } from '../drawing.helper'
import { getTradingSessionsForDate, CalendarData } from '../../../../util/chart'
import { getLineDashPattern, ChartLineStyle } from '../../../colorSelector/lineStyle.types'
import { TimeBucket_TimeSeries } from '../../../../proto/generated/common'
import { toDayStart } from '../../../../util/dateTime'
import { ChartCandle } from '../../chart.typings'
import SessionBreakPaneView from './sessionBreak.paneView'

export type SessionBreakType = 'sessionBreak'

export type SessionBreakState = {
    visible: boolean
    color: string
    lineStyle: string
}

export type SessionBreakOptions = {
    drawingType: SessionBreakType
}

export type SessionBreakDrawingSettings = {
    visible?: boolean
    color?: string
    lineStyle?: string
}

type SessionBreakPosition = {
    prevTime: number
    nextTime: number
}

const collectSessionBreakPositions = (
    data: ChartCandle[],
    calendarData: CalendarData
): SessionBreakPosition[] => {
    if (data.length < 2) return []

    const breaks: SessionBreakPosition[] = []
    const seen = new Set<string>()

    const addBreak = (prevTime: number, nextTime: number) => {
        const key = `${prevTime}:${nextTime}`
        if (seen.has(key)) return
        seen.add(key)
        breaks.push({ prevTime, nextTime })
    }

    const seenDays = new Set<number>()
    data.forEach((candle) => {
        const candleTime = candle.time as number
        const dayStart = Math.floor(toDayStart(candleTime * 1000).getTime() / 1000)
        if (seenDays.has(dayStart)) return
        seenDays.add(dayStart)

        const sessions = getTradingSessionsForDate(dayStart, calendarData)
        sessions.forEach((session) => {
            const boundaryTime = dayStart + session.startTime
            for (let j = 1; j < data.length; j++) {
                const prev = data[j - 1].time as number
                const curr = data[j].time as number
                if (prev < boundaryTime && curr >= boundaryTime) {
                    addBreak(prev, curr)
                    break
                }
            }
        })
    })

    return breaks
}

class SessionBreakTool extends DrawingBase<SessionBreakOptions, SessionBreakDrawingSettings, SessionBreakState> {
    fixedDrawing = true

    constructor(options: DrawingOptions<any, any, any>) {
        super(options)
        this.paneViews = [new SessionBreakPaneView({ id: this._paneId || 'primary', drawing: this })]
        if (!this._chartApi) return
        this._chartApi.timeScale().subscribeVisibleTimeRangeChange(() => {
            this.primitive?.requestUpdate()
        })
        setTimeout(() => this.primitive?.requestUpdate(), 0)
    }

    private isVisible() {
        return this._state.visible ?? this._settings.visible ?? true
    }

    private getLineColor() {
        return this._state.color ?? this._settings.color ?? '#2962FF'
    }

    private getLineStyle() {
        return (this._state.lineStyle ?? this._settings.lineStyle ?? 'dashed') as ChartLineStyle
    }

    draw(scope: BitmapCoordinatesRenderingScope) {
        if (!this.isVisible()) return

        const timeBucket = this._chart.timeBucket
        if (!timeBucket || timeBucket.timeSeries === TimeBucket_TimeSeries.DAY) return

        const calendarData = this._chart.calendarData
        if (!calendarData) return

        const primaryData = this._chart.getRenderedPrimaryData() as ChartCandle[]
        if (!primaryData.length) return

        const breakPositions = collectSessionBreakPositions(primaryData, calendarData)
        if (!breakPositions.length) return

        const ctx = scope.context
        const lineColor = this.getLineColor()
        const lineWidth = this.horizontalPixelRatio
        const dashPattern = getLineDashPattern(this.getLineStyle(), lineWidth)

        breakPositions.forEach(({ prevTime, nextTime }) => {
            const x1 = this.timeToCoordinate(prevTime as Time, true)
            const x2 = this.timeToCoordinate(nextTime as Time, true)
            if (x1 === null || x1 === undefined || x2 === null || x2 === undefined) return

            const x = (x1 + x2) / 2
            const xCoordinate = scaledCoordinate(x, this.horizontalPixelRatio)
            if (xCoordinate === null || xCoordinate === undefined || Number.isNaN(xCoordinate)) return

            ctx.save()
            ctx.beginPath()
            ctx.strokeStyle = lineColor
            ctx.lineWidth = lineWidth
            if (dashPattern.length) {
                ctx.setLineDash(dashPattern)
            }
            ctx.moveTo(xCoordinate, 0)
            ctx.lineTo(xCoordinate, this.canvasHeight)
            ctx.stroke()
            ctx.restore()
        })
    }

    onSettingsUpdate = (settings: Partial<SessionBreakDrawingSettings>) => {
        if (settings.visible !== undefined) {
            this._state.visible = settings.visible
            this._settings.visible = settings.visible
        }
        if (settings.color !== undefined) {
            this._state.color = settings.color
            this._settings.color = settings.color
        }
        if (settings.lineStyle !== undefined) {
            this._state.lineStyle = settings.lineStyle
            this._settings.lineStyle = settings.lineStyle
        }
        this.primitive?.requestUpdate()
    }

    getAvailableSettings = (): DrawingSettingGroup<SessionBreakDrawingSettings, SettingValues>[] => []

    getDrawingType(): DrawingTypes {
        return 'sessionBreak'
    }
}

export default SessionBreakTool
